import Filters, { FilterComponent } from "@/components/Filters"
import { Paths } from "@/constants"
import { deleteInventory, fetchInventories } from "@/modules/warehouses/inventories/services/inventories.services"
import { fetchUnits } from "@/modules/warehouses/inventories/services/units.services"
import { InventoryType, UnitType } from "@/modules/warehouses/inventories/types"
import UpsertForm from "@/modules/warehouses/inventories/view/UpsertForm"
import { fetchInventoryCategories } from "@/modules/warehouses/inventory-categories/services/inventoryCategories.services"
import { ResponseData } from "@/types"
import { normalizeText } from "@/utils"
import { MAP_STATUS, StatusEnum } from "@/utils/constants"
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons"
import { Button, Card, Flex, Popconfirm, Space, Table, Tag, Typography } from "antd"
import { SorterResult } from "antd/es/table/interface"
import dayjs from "dayjs"
import queryString from "query-string"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { AnyObject } from "yup/lib/types"

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>

const List: React.FC = () => {
  const [dataSource, setDataSource] = useState<ResponseData<InventoryType>>()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [needRefresh, setNeedRefresh] = useState(false)
  const [idEdit, setIdEdit] = useState<string>("")
  const [actionType, setActionType] = useState<string>("")
  const [currentPage, setCurrentPage] = useState(1)
  const navigate = useNavigate()
  const location = useLocation()
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)
  const [dataUnits, setDataUnits] = useState<UnitType[]>()
  const [optionInventory, setOptionInventory] = useState<{ value: string | number; label: string }[]>([])
  const [optionInvetoryCategory, setOptionInvetoryCategory] = useState<{ value: string | number; label: string }[]>([])

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    setNeedRefresh(!needRefresh)
  }

  const onDismissModal = () => {
    setOpen(false)
  }

  const onTableChanged = ((pagination, _, sorter: SorterResult<AnyObject>) => {
    const sortOptions: { [key: string]: string } = sorter.order
      ? { [sorter.field as string]: sorter.order === "descend" ? "desc" : "asc" }
      : {}
    const paginationOptions: { [key: string]: string } = {
      page: `${pagination.current}`,
      limit: `${pagination.pageSize}`,
    }

    if (pagination && pagination.current) {
      setCurrentPage(pagination.current)
    }

    setSortPaginationValues({ ...sortOptions, ...paginationOptions })
    const searchParams = queryString.stringify({ ...filterValues, ...sortOptions, ...paginationOptions })

    navigate(searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname)
  }) as EditableTableProps["onChange"]

  const showModal = () => {
    setActionType("CREATE")
    setOpen(true)
  }

  const showModalEdit = (id: string) => {
    navigate(`${Paths.INVENTORY.LIST.PATH}/${id}${location.search}`)
    setIdEdit(id)
    setOpen(true)
    setActionType("UPDATE")
  }

  const onSearch = useCallback(
    (values: any) => {
      let newValue = { ...values, ...sortPaginationValues }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      if (newValue.min_created_at) {
        newValue = { ...newValue, min_created_at: dayjs(newValue.min_created_at).format("YYYY-MM-DD") }
      }

      if (newValue.max_created_at) {
        newValue = { ...newValue, max_created_at: dayjs(newValue.max_created_at).format("YYYY-MM-DD") }
      }

      const searchParams = queryString.stringify(newValue)
      const newUrl = searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname
      navigate(newUrl)
    },
    [sortPaginationValues],
  )

  useEffect(() => {
    if (!filterValues && !sortPaginationValues) return
    const params = { ...filterValues, ...sortPaginationValues }
    setLoading(true)
    fetchInventories(params).then((res: any) => {
      if (sortPaginationValues && sortPaginationValues.page) {
        if (sortPaginationValues.page > res.data.meta.totalPages && res.data.meta.totalPages > 0) {
          const searchLocation = queryString.parse(location.search)
          const newParams = queryString.stringify({ ...searchLocation, page: `${res.data.meta.totalPages}` })
          const newUrl = newParams.length > 0 ? `${location.pathname}?${newParams}` : location.pathname
          navigate(newUrl)
        }
      }

      setDataSource(res.data)
      setLoading(false)
    })
  }, [filterValues, sortPaginationValues, needRefresh])

  useEffect(() => {
    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: any } = {}
    const sortPaginationValues: { [key: string]: string } = {}
    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "page" || key === "limit" || value === "asc" || value === "desc") {
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setFilterValues(filterValues)
    setSortPaginationValues(sortPaginationValues)
  }, [location.search])

  useEffect(() => {
    fetchUnits({ limit: 1000 }).then((res: any) => {
      if (res.data && res.data.length) {
        setDataUnits(res.data)
      }
    })

    fetchInventories({ limit: 1000 }).then((res: any) => {
      if (res.data.items && res.data.items.length) {
        const mappedInventories = res.data.items.map((item: InventoryType) => ({ value: item.id, label: item.name }))
        setOptionInventory(mappedInventories)
      }
    })
    fetchInventoryCategories({ limit: 1000 }).then((res: any) => {
      if (res.data.items?.length) {
        const mappedInventories = res.data.items.map((item: InventoryType) => ({ value: item.id, label: item.name }))
        setOptionInvetoryCategory(mappedInventories)
      }
    })

    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: any } = {}
    const sortPaginationValues: { [key: string]: string } = {}
    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "min_created_at" || key === "max_created_at") {
          filterValues[key] = dayjs(value)
          continue
        }
        if (value === "true" || value === "false") {
          filterValues[key] = value === "true" ? true : false
          continue
        }
        if (key === "page" || key === "limit" || value === "asc" || value === "desc") {
          if (key === "page") {
            setCurrentPage(parseInt(value))
          }
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setDefaultFilterValues({ ...filterValues, ...sortPaginationValues })
  }, [])
  const showModalCreateInventoryTransaction = (id: string, type: string) => {
    navigate(`${Paths.INVENTORY.LIST.PATH}/${id}/transactions?type=${type}`)
  }

  const handleDelete = async (record: InventoryType) => {
    await deleteInventory(record)
    setNeedRefresh(!needRefresh)
  }

  const defaultColumns: (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: "5%",
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Tên hàng hoá",
      dataIndex: "name",
      key: "name",
      width: "14%",
      sorter: (a, b) => a.name - b.name,
    },
    {
      title: "Số lượng",
      dataIndex: "quantity",
      key: "quantity",
      width: "8%",
      render: quantity => (quantity !== null ? quantity : null),
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      title: "Đơn vị",
      dataIndex: "unit_id",
      key: "unit_id",
      width: "8%",
      render: unit_id => dataUnits?.find(unit => unit.id === unit_id)?.name,
      sorter: (a, b) => a.unit_id - b.unit_id,
    },
    {
      title: "Loại hàng",
      dataIndex: "category_id",
      key: "category_id",
      width: "8%",
      render: category_id => optionInvetoryCategory?.find(category => category.value === category_id)?.label,
      sorter: (a, b) => a.unit_id - b.unit_id,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      render: (status: StatusEnum) => {
        return <Tag color={status === StatusEnum.ACTIVE ? "success" : "error"}>{MAP_STATUS[status]}</Tag>
      },
      width: "8%",
    },
    {
      title: "Ngày tạo",
      dataIndex: "created_at",
      key: "created_at",
      render: created_at => created_at && dayjs(new Date(created_at)).format("HH:mm DD/MM/YYYY"),
      width: "10%",
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: "Ngày cập nhật",
      dataIndex: "updated_at",
      key: "updated_at",
      render: updated_at => updated_at && dayjs(new Date(updated_at)).format("HH:mm DD/MM/YYYY"),
      width: "10%",
      sorter: (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      render: (_, record) =>
        dataSource && dataSource?.items.length >= 1 ? (
          <Space>
            <Button
              ghost
              type="primary"
              shape="round"
              size="small"
              onClick={() => showModalCreateInventoryTransaction(record?.id, "in")}
            >
              Nhập kho
            </Button>
            <Button
              ghost
              type="primary"
              shape="round"
              size="small"
              onClick={() => showModalCreateInventoryTransaction(record?.id, "out")}
              disabled={record?.quantity === 0}
            >
              Xuất kho
            </Button>
            <Button ghost type="primary" shape="round" size="small" onClick={() => showModalEdit(record?.id)}>
              Sửa
            </Button>
            <Popconfirm title="Bạn có chắc chắn muốn xóa?" onConfirm={() => handleDelete(record as InventoryType)}>
              <Button type="default" shape="round" danger size="small">
                Xóa
              </Button>
            </Popconfirm>
          </Space>
        ) : null,
      width: "21%",
    },
  ]

  const columns = defaultColumns.map(col => {
    if (!col.editable) {
      return col
    }

    return {
      ...col,
      onCell: (record: InventoryType) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
      }),
    }
  })

  return (
    <div>
      <Card>
        <Flex justify="space-between" style={{ flexWrap: "wrap" }}>
          <Typography.Title level={4} style={styles.title}>
            Danh sách hàng hóa trong kho hàng
          </Typography.Title>
          <Space style={{ flexWrap: "wrap", marginTop: "10px" }}>
            <Button onClick={onRefresh} icon={<ReloadOutlined />}>
              Làm mới
            </Button>
            <Button type="primary" onClick={() => showModal()} icon={<PlusOutlined />}>
              Thêm hàng hóa
            </Button>
          </Space>
        </Flex>
        <Card type="inner" style={styles.card}>
          <Filters
            filterRef={filterRef}
            defaultFilterValues={defaultFilterValues}
            onSubmit={onSearch}
            filters={
              [
                {
                  label: "Tìm hàng hoá",
                  placeholder: "Tên hàng hoá",
                  name: "id",
                  type: "select-search",
                  options: optionInventory,
                },
                {
                  label: "Loại hàng",
                  placeholder: "Loại hàng hoá",
                  name: "category_id",
                  type: "select-search",
                  options: optionInvetoryCategory,
                },
              ] as FilterComponent[]
            }
          />
        </Card>

        <UpsertForm
          openProps={open}
          cancelProps={onDismissModal}
          refreshProps={onRefresh}
          actionType={actionType}
          idEdit={idEdit}
        />
        <Table
          rowClassName={() => "editable-row"}
          bordered
          dataSource={dataSource?.items}
          columns={columns as ColumnTypes}
          loading={loading}
          scroll={{ x: 1200, y: 400 }}
          onChange={onTableChanged}
          pagination={{
            total: dataSource?.meta.totalItems,
            pageSize: dataSource?.meta.itemsPerPage,
            current: currentPage,
            responsive: true,
          }}
          rowKey={record => record.id}
        />
      </Card>
    </div>
  )
}

export default List

const styles = {
  title: {
    marginTop: 0,
    marginBottom: 0,
  },
  card: {
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
  },
  "@media (max-width: 375px)": {
    title: {
      fontSize: "1.25rem",
    },
    card: {
      marginTop: "0.5rem",
      marginBottom: "0.5rem",
      padding: "0.5rem",
    },
  },
}
